import React, { ChangeEvent, Component, ReactNode } from "react";
import { observer } from "mobx-react";

import { DeviceFileType } from "@formant/core/model/DeviceFileType";
import { deviceFileTypes } from "@formant/core/model/deviceFileTypes";
import { HardwareType } from "@formant/core/model/HardwareType";
import { hardwareTypes } from "@formant/core/model/hardwareTypes";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceStreamConfiguration } from "@formant/core/model/IDeviceStreamConfiguration";
import { ITags } from "@formant/core/model/ITags";
import { IValidation } from "@formant/core/model/IValidation";
import {
    rosTypeGoalID,
    rosTypePoseStamped,
} from "@formant/core/model/rosTypes";
import { isValidRosVersion, RosVersion } from "@formant/core/model/RosVersion";
import { rosVersions } from "@formant/core/model/rosVersions";
import { TeleopRosTopicType } from "@formant/core/model/TeleopRosTopicType";
import { InputElement } from "@formant/ui/component/common/Field";
import { Icon } from "@formant/ui/component/common/Icon";
import { TextField } from "@formant/ui/component/common/TextField";

import { audioPathsMapForDevices } from "../../utils/audioPathsMapForDevice";
import { cleanHardwareName } from "../../utils/cleanHardwareName";
import { videoPathsMapForDevices } from "../../utils/videoPathsMapForDevice";
import { Button } from "../common/Button";
import { Buttons } from "../common/Buttons";
import { EditDeviceTags } from "../common/EditDeviceTags";
import { Heading3 } from "../common/Heading3";
import { Help } from "../common/Help";
import { SelectField } from "../common/SelectField";
import { Switch } from "../common/Switch";
import { TextButton } from "../common/TextButton";
import { rosTopicsForDevices } from "../Observe/utils/rosTopicsForDevices";
import * as styles from "./EditStreamDetails.scss";

export interface IEditStreamDetailsProps {
    devices?: IDevice[];
    document: IDeviceConfigurationDocument;
    stream: IDeviceStreamConfiguration;
    validation: IValidation;
    onChange: (document: IDeviceConfigurationDocument) => void;
    onDelete?: (stream: IDeviceStreamConfiguration) => void;
}

@observer
export class EditStreamDetails extends Component<IEditStreamDetailsProps> {
    public render() {
        const { document, devices, stream, validation, onDelete } = this.props;
        const { configuration, tags } = stream;

        const pointCloudTopics =
            configuration.type === "ros-localization"
                ? (configuration.pointCloudTopics || []).join(", ")
                : "";

        const rosTopics = rosTopicsForDevices(devices);

        const teleopTopics = document.teleop?.rosStreams || [];

        const teleopGoalTopic = teleopTopics.find(
            ({ topicType, mode }) =>
                topicType === rosTypePoseStamped && mode === "command",
        );

        const teleopCancelTopic = teleopTopics.find(
            ({ topicType, mode }) =>
                topicType === rosTypeGoalID && mode === "command",
        );
        const videoPathsMap = videoPathsMapForDevices(devices);
        const audioPathsMap = audioPathsMapForDevices(devices);
        const isIP =
            configuration.type === "hardware" &&
            configuration.hardwareType === "ip";

        let details: ReactNode = null;

        switch (configuration.type) {
            case "directory-watch":
                details = (
                    <>
                        <p>
                            Absolute file path. Must be read-accessible to
                            device process.
                        </p>
                        <TextField
                            className={styles.field}
                            label="Directory name"
                            data-testid="directory-name-input"
                            small={true}
                            value={configuration.directory}
                            invalid={!!validation?.["configuration.directory"]}
                            onChange={this.onChangeDirectory(stream)}
                        />
                        <p>
                            Optional file extension. Must start with period (
                            <code>.</code>). For example, <code>.png</code>.
                        </p>
                        <TextField
                            className={styles.field}
                            label="Extension"
                            data-testid="file-extension-input"
                            small={true}
                            value={configuration.extension || ""}
                            invalid={!!validation?.["configuration.extension"]}
                            onChange={this.onChangeExtension(stream)}
                        />
                        <p>
                            Type of telemetry stream to create.{" "}
                            <code>File</code> ingests each new file in the
                            directory as a <code>file</code> data point.{" "}
                            <code>Image</code> ingests each new file as an{" "}
                            <code>image</code> data point (supports{" "}
                            <code>.jpg</code> and <code>.png</code> files).{" "}
                            <code>Point Cloud</code> ingests each new file as a{" "}
                            <code>point cloud</code> data point (supports{" "}
                            <code>.pcd</code> files).
                        </p>
                        <SelectField
                            className={styles.field}
                            label="File type"
                            data-testid="file-type-select"
                            small={true}
                            value={configuration.fileType}
                            invalid={!!validation?.["configuration.fileType"]}
                            onChange={this.onChangeFileType(stream)}
                        >
                            {deviceFileTypes.map(_ => (
                                <option key={_} value={_}>
                                    {deviceFileTypeToString(_)}
                                </option>
                            ))}
                        </SelectField>
                    </>
                );
                break;
            case "file-tail":
                details = (
                    <>
                        <p>
                            Absolute directory path. Must be read-accessible to
                            device process.
                        </p>
                        <TextField
                            className={styles.field}
                            label="File name"
                            data-testid="file-name-input"
                            small={true}
                            value={configuration.filename}
                            invalid={!!validation?.["configuration.filename"]}
                            onChange={this.onChangeFilename(stream)}
                        />
                        <p>
                            Lines in the file that match this{" "}
                            <TextButton
                                target="_blank"
                                to="https://github.com/google/re2/wiki/Syntax"
                            >
                                regular expression
                            </TextButton>{" "}
                            will be ingested.
                        </p>
                        <TextField
                            className={styles.field}
                            label="Regular expression"
                            data-testid="regular-expression-input"
                            small={true}
                            value={configuration.regex || ""}
                            invalid={!!validation?.["configuration.regex"]}
                            onChange={this.onChangeRegex(stream)}
                        />
                    </>
                );
                break;
            case "ros-topic":
                details = (
                    <>
                        <>
                            <p>
                                ROS version
                                <Help text="The ROS version of the node publishing the topic." />
                            </p>
                            <SelectField
                                value={configuration.rosVersion}
                                onChange={this.onChangeTopicRosVersion(stream)}
                                small={true}
                            >
                                {rosVersions.map(_ => (
                                    <option key={_} value={_}>
                                        {getRosVersionLabel(_)}
                                    </option>
                                ))}
                            </SelectField>
                        </>
                        <p>
                            Name of ROS topic to ingest.
                            <Help
                                text="Click for docs."
                                to="https://docs.formant.io/docs/getting-started-add-a-ros-topic-stream"
                            />
                        </p>
                        <TextField
                            className={styles.field}
                            label="Ingestion topic"
                            data-testid="ingestion-topic-input"
                            small={true}
                            value={configuration.topic}
                            list={rosTopics.map(_ => _.name)}
                            invalid={!!validation?.["configuration.topic"]}
                            onChange={this.onChangeTopic(stream)}
                        />
                        <p>
                            Optional path within ROS message to desired data
                            (e.g., to get only the `x` component of a linear
                            field in a `Twist`, use `linear.x`).
                            <Help
                                text="Click for docs."
                                to="https://docs.formant.io/docs/getting-started-add-a-ros-topic-stream"
                            />
                        </p>
                        <TextField
                            className={styles.field}
                            label="ROS message path"
                            data-testid="ros-message-path-input"
                            small={true}
                            value={configuration.path || ""}
                            invalid={!!validation?.["configuration.path"]}
                            onChange={this.onChangePath(stream)}
                        />
                    </>
                );
                break;
            case "ros-localization":
                details = (
                    <div className={styles.localization}>
                        <>
                            <p>
                                ROS version
                                <Help text="The ROS version of the node publishing the topic." />
                            </p>
                            <SelectField
                                value={configuration.rosVersion}
                                onChange={this.onChangeTopicRosVersion(stream)}
                                small={true}
                            >
                                {rosVersions.map(_ => (
                                    <option key={_} value={_}>
                                        {getRosVersionLabel(_)}
                                    </option>
                                ))}
                            </SelectField>
                        </>

                        <Heading3>Required</Heading3>
                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Base reference frame"
                                data-testid="base-reference-frame-input"
                                placeholder="Base reference frame (eg. map)"
                                small={true}
                                value={configuration.baseReferenceFrame}
                                invalid={
                                    !!validation?.[
                                        "configuration.baseReferenceFrame"
                                    ]
                                }
                                onChange={this.onChangeBaseReferenceFrame(
                                    stream,
                                )}
                            />
                            <Help
                                className={styles.help}
                                text="Base reference frame. Used to visualize localization state without ingesting the entire transform tree. All topics in localization must have a frame under the transform tree (tf2)."
                            />
                        </div>

                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Odometry topic"
                                data-testid="odometry-topic-input"
                                small={true}
                                value={configuration.odomTopic}
                                list={rosTopics
                                    .filter(_ => _.type === "nav_msgs/Odometry")
                                    .map(_ => _.name)}
                                invalid={
                                    !!validation?.["configuration.odomTopic"]
                                }
                                onChange={this.onChangeOdomTopic(stream)}
                            />
                            <Help
                                className={styles.help}
                                text="Add an odometry topic to position the map relative to the device's position. nav_msgs/Odometry"
                            />
                        </div>

                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Map topic"
                                data-testid="map-topic-input"
                                small={true}
                                value={configuration.mapTopic}
                                list={rosTopics
                                    .filter(
                                        _ =>
                                            _.type === "nav_msgs/OccupancyGrid",
                                    )
                                    .map(_ => _.name)}
                                invalid={
                                    !!validation?.["configuration.mapTopic"]
                                }
                                onChange={this.onChangeMapTopic(stream)}
                            />
                            <Help
                                className={styles.help}
                                text="Add a map topic to view the device's occupancy grid. nav_msgs/OccupancyGrid"
                            />
                        </div>

                        <Heading3>Optional</Heading3>

                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Point cloud topics"
                                data-testid="point-could-topics-input"
                                small={true}
                                value={pointCloudTopics}
                                list={rosTopics
                                    .filter(
                                        _ =>
                                            _.type ===
                                                "sensor_msgs/PointCloud2" ||
                                            _.type === "sensor_msgs/LaserScan",
                                    )
                                    .map(_ => _.name)}
                                invalid={
                                    !!Object.keys(validation).find(_ =>
                                        _.startsWith(
                                            "configuration.pointCloudTopics",
                                        ),
                                    )
                                }
                                onChange={this.onChangePointCloudTopics(stream)}
                            />
                            <Help
                                className={styles.help}
                                text="Add a comma-separated list of point cloud / laser scan topics to add a point cloud overlay to the localization stream. sensor_msgs/PointCloud2, sensor_msgs/LaserScan"
                            />
                        </div>

                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Path"
                                data-testid="path-input"
                                small={true}
                                value={configuration.pathTopic}
                                list={rosTopics
                                    .filter(_ => _.type === "nav_msgs/Path")
                                    .map(_ => _.name)}
                                invalid={
                                    !!validation?.["configuration.pathTopic"]
                                }
                                onChange={this.onChangePathTopic(stream)}
                            />
                            <Help
                                className={styles.help}
                                text="If using path planning, add a plan topic to show the device's path. nav_msgs/Path"
                            />
                        </div>

                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Goal"
                                data-testid="goal-input"
                                small={true}
                                value={configuration.goalTopic}
                                list={rosTopics
                                    .filter(
                                        _ =>
                                            _.type ===
                                            "geometry_msgs/PoseStamped",
                                    )
                                    .map(_ => _.name)}
                                invalid={
                                    !!validation?.["configuration.goalTopic"]
                                }
                                onChange={this.onChangeGoalTopic(stream)}
                            />
                            <Help
                                className={styles.help}
                                text="If using path planning, add a goal topic to show the device's current goal. geometry_msgs/PoseStamped"
                            />
                        </div>

                        <Heading3>Teleoperation</Heading3>

                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Publish goal"
                                data-testid="publish-goal-input"
                                small={true}
                                value={teleopGoalTopic?.topicName || ""}
                                list={rosTopics
                                    .filter(
                                        _ =>
                                            _.type ===
                                            "geometry_msgs/PoseStamped",
                                    )
                                    .map(_ => _.name)}
                                onChange={this.onChangeGoalPublishTopic}
                            />
                            <Help
                                className={styles.help}
                                text="To enable minimap navigation, add a goal topic to publish to, and a goal ID topic for canceling the current goal. geometry_msgs/PoseStamped"
                            />
                        </div>

                        <div className={styles["field-row"]}>
                            <TextField
                                className={styles.field}
                                label="Cancel goal"
                                data-testid="cancel-goal-input"
                                small={true}
                                value={teleopCancelTopic?.topicName || ""}
                                list={rosTopics
                                    .filter(
                                        _ => _.type === "actionlib_msgs/GoalID",
                                    )
                                    .map(_ => _.name)}
                                onChange={this.onChangeGoalCancelTopic}
                            />
                            <Help
                                className={styles.help}
                                text="To enable minimap navigation, add a goal topic to publish to, and a goal ID topic for canceling the current goal. actionlib_msgs/GoalID"
                            />
                        </div>
                    </div>
                );
                break;
            case "ros-transform-tree":
                details = (
                    <>
                        <>
                            <p>
                                ROS version
                                <Help text="The ROS version of the node publishing the topic." />
                            </p>
                            <SelectField
                                value={configuration.rosVersion}
                                onChange={this.onChangeTopicRosVersion(stream)}
                                small={true}
                            >
                                {rosVersions.map(_ => (
                                    <option key={_} value={_}>
                                        {getRosVersionLabel(_)}
                                    </option>
                                ))}
                            </SelectField>
                        </>

                        <p>
                            The base reference frame must be included in{" "}
                            <code>/tf</code> or <code>/tf_static</code>. The
                            agent limits the amount of edges it collects to 100.
                            An edge is defined as a parent/child relationship in
                            the transform tree.
                        </p>
                        <TextField
                            className={styles.field}
                            label="Base reference frame"
                            placeholder="Base reference frame (eg. base_link)"
                            small={true}
                            value={configuration.baseReferenceFrame}
                            invalid={
                                !!validation?.[
                                    "configuration.baseReferenceFrame"
                                ]
                            }
                            onChange={this.onChangeBaseReferenceFrame(stream)}
                            data-testid="base-reference-input"
                        />
                    </>
                );
                break;
            case "custom":
                details = null;
                break;

            case "hardware":
                details = (
                    <>
                        <p>Type of Video Hardware Device.</p>
                        <SelectField
                            small={true}
                            label="Hardware type"
                            data-testid="hardware-type-select"
                            value={configuration.hardwareType}
                            onChange={this.onChangeHardwareType(stream)}
                        >
                            {hardwareTypes.map(_ => (
                                <option key={_} value={_}>
                                    {_}
                                </option>
                            ))}
                        </SelectField>
                        <p>Path of Video Hardware Device.</p>
                        <TextField
                            value={configuration.hwDescriptor}
                            onChange={this.onChangeNewHardwareStreamDevice(
                                videoPathsMap,
                                stream,
                            )}
                            small={true}
                            className={styles.field}
                            label="Video hardware"
                            data-testid="video-hardware-input"
                            placeholder={
                                isIP
                                    ? "Video RTSP URI e.g. 'rtsp://192.168.1.110:554/h264'"
                                    : "Video Hardware path e.g. '/dev/video0'"
                            }
                            autoFocus={false}
                            list={Object.keys(
                                videoPathsMap[configuration.hardwareType] ?? {},
                            )}
                        />
                        {isIP && (
                            <>
                                {" "}
                                <Switch
                                    state={
                                        configuration.rtspEncodingNeeded ??
                                        false
                                    }
                                    onChange={this.onChangeRTSPEncodingNeeded(
                                        stream,
                                    )}
                                    label="Encoding required"
                                    stateLabels={["No", "Yes"]}
                                    info="If the IP camera source is outputting h264, encoding should be disabled."
                                    className={styles.switch}
                                />
                                <Switch
                                    state={configuration.isOnvif ?? false}
                                    onChange={this.onChangeIsOnvif(stream)}
                                    label="Supports ONVIF"
                                    stateLabels={["No", "Yes"]}
                                    info="If the camera supports ONVIF protocol"
                                    className={styles.switch}
                                />
                                <TextField
                                    value={configuration.ipCamUsername}
                                    onChange={this.onChangeIpCamUsername(
                                        stream,
                                    )}
                                    className={styles.field}
                                    label="Username"
                                    placeholder="Username e.g. admin"
                                    small={true}
                                />
                                <TextField
                                    value={configuration.ipCamPassword}
                                    onChange={this.onChangeIpCamPassword(
                                        stream,
                                    )}
                                    className={styles.field}
                                    label="Password"
                                    placeholder="Password e.g. 123456"
                                    small={true}
                                />
                            </>
                        )}
                        <p>Name of Audio Hardware Device.</p>
                        <TextField
                            value={configuration.audioHwDescriptor}
                            onChange={this.onChangeNewHardwareStreamAudioDevice(
                                audioPathsMap,
                                stream,
                            )}
                            small={true}
                            label="Audio hardware"
                            placeholder="Audio Hardware name"
                            list={Object.keys(audioPathsMap ?? {})}
                        />
                    </>
                );
                break;
        }

        return (
            <div className={styles["edit-stream-details"]}>
                {onDelete && (
                    <Buttons className={styles.buttons}>
                        <Button
                            className={styles["delete-stream"]}
                            context={stream}
                            onClicked={onDelete}
                        >
                            <Icon name="delete" className={styles.delete} />
                        </Button>
                    </Buttons>
                )}
                {details}
                <>
                    <p>
                        Optional tags to add to each data point of this stream.
                        <Help
                            text="Click for docs."
                            to="https://docs.formant.io/docs/getting-started-add-a-ros-topic-stream"
                        />
                    </p>
                    <EditDeviceTags
                        value={tags || {}}
                        small={true}
                        className={styles.field}
                        onChange={this.onChangeTags(stream)}
                        skipUnusedTags={true}
                    />
                </>
            </div>
        );
    }
    private onChangeNewHardwareStreamAudioDevice =
        (
            audioPathsMap: { [id: string]: string },
            stream: IDeviceStreamConfiguration,
        ) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type === "hardware") {
                const name = event.target.value;
                let audioHwDescriptor = event.target.value;
                if (audioPathsMap !== undefined) {
                    if (audioPathsMap[name] !== undefined) {
                        audioHwDescriptor = audioPathsMap[name];
                    }
                }
                configuration.audioHwDescriptor = audioHwDescriptor;
            }
        };

    private onChangeHardwareType =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<HTMLSelectElement>) => {
            const { configuration } = stream;
            const hardwareType = event.target.value as HardwareType;
            if (configuration.type === "hardware") {
                configuration.hardwareType = hardwareType;
                stream.name = cleanHardwareName(
                    stream.name,
                    configuration.hardwareType,
                );
            }
        };
    private onChangeRTSPEncodingNeeded =
        (stream: IDeviceStreamConfiguration) => (newValue: boolean) => {
            const { configuration } = stream;
            if (configuration.type === "hardware") {
                configuration.rtspEncodingNeeded = newValue;
            }
        };

    private onChangeIsOnvif =
        (stream: IDeviceStreamConfiguration) => (newValue: boolean) => {
            const { configuration } = stream;
            if (configuration.type === "hardware") {
                configuration.isOnvif = newValue;
            }
        };
    private onChangeIpCamUsername =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type === "hardware") {
                configuration.ipCamUsername = event.target.value;
            }
        };
    private onChangeIpCamPassword =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type === "hardware") {
                configuration.ipCamPassword = event.target.value;
            }
        };
    private onChangeNewHardwareStreamDevice =
        (
            videoPathsMap: { [id: string]: { [id: string]: string } },
            stream: IDeviceStreamConfiguration,
        ) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type === "hardware") {
                const name = event.target.value;
                let hwDescriptor = event.target.value;
                if (videoPathsMap !== undefined) {
                    if (
                        videoPathsMap[configuration.hardwareType][name] !==
                        undefined
                    ) {
                        hwDescriptor =
                            videoPathsMap[configuration.hardwareType][name];
                        this.onChangeIsOnvif(stream)(true);
                    }
                }
                configuration.hwDescriptor = hwDescriptor;
                if (stream.name === "") {
                    stream.name = cleanHardwareName(
                        name,
                        configuration.hardwareType,
                    );
                }
            }
        };

    private onChangeDirectory =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "directory-watch") {
                return;
            }
            configuration.directory = event.target.value;
        };

    private onChangeExtension =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "directory-watch") {
                return;
            }
            configuration.extension = event.target.value;
        };

    private onChangeFileType =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "directory-watch") {
                return;
            }
            configuration.fileType = event.target.value as DeviceFileType;
        };

    private onChangeFilename =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "file-tail") {
                return;
            }
            configuration.filename = event.target.value;
        };

    private onChangeRegex =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "file-tail") {
                return;
            }
            configuration.regex = event.target.value;
        };

    private onChangeOdomTopic =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "ros-localization") {
                return;
            }
            configuration.odomTopic = event.target.value;
        };

    private onChangeMapTopic =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "ros-localization") {
                return;
            }
            const { value } = event.target;
            configuration.mapTopic = value !== "" ? value : undefined;
        };

    private onChangePointCloudTopics =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "ros-localization") {
                return;
            }
            const { value } = event.target;
            const pointCloudTopics = value
                .split(",")
                .map(_ => _.trim())
                .filter(_ => _);

            if (
                value.endsWith(",") &&
                configuration.pointCloudTopics?.slice(-1)[0]
            ) {
                // we need to let the user input additional topics so
                // if a trailing commas is entered, append an empty topic name
                pointCloudTopics.push("");
            }

            configuration.pointCloudTopics =
                pointCloudTopics.length > 0 ? pointCloudTopics : undefined;
        };

    private onChangePathTopic =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "ros-localization") {
                return;
            }
            const { value } = event.target;
            configuration.pathTopic = value !== "" ? value : undefined;
        };

    private onChangeGoalTopic =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "ros-localization") {
                return;
            }
            const { value } = event.target;
            configuration.goalTopic = value !== "" ? value : undefined;
        };

    private onChangeBaseReferenceFrame =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (
                configuration.type !== "ros-transform-tree" &&
                configuration.type !== "ros-localization"
            ) {
                return;
            }
            configuration.baseReferenceFrame = event.target.value;
        };

    private onChangeTopicRosVersion =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            const { value } = event.target;
            if (
                configuration.type !== "ros-topic" &&
                configuration.type !== "ros-transform-tree" &&
                configuration.type !== "ros-localization"
            ) {
                return;
            }
            if (isValidRosVersion(value)) {
                configuration.rosVersion = value as RosVersion;
            }
        };

    private onChangeTopic =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "ros-topic") {
                return;
            }
            configuration.topic = event.target.value;
        };
    private onChangePath =
        (stream: IDeviceStreamConfiguration) =>
        (event: ChangeEvent<InputElement>) => {
            const { configuration } = stream;
            if (configuration.type !== "ros-topic") {
                return;
            }
            configuration.path = event.target.value;
        };

    private onChangeTags =
        (stream: IDeviceStreamConfiguration) => (tags: ITags) => {
            stream.tags = tags;
        };

    private onChangeGoalPublishTopic = (event: ChangeEvent<InputElement>) => {
        this.onChangeTeleopTopic(event.target.value, rosTypePoseStamped);
    };

    private onChangeGoalCancelTopic = (event: ChangeEvent<InputElement>) => {
        this.onChangeTeleopTopic(event.target.value, rosTypeGoalID);
    };

    private onChangeTeleopTopic = (value: string, type: TeleopRosTopicType) => {
        const { document, onChange } = this.props;
        let rosStreams = document.teleop?.rosStreams || [];
        const teleop = document.teleop || {};

        const teleopGoalTopic = rosStreams.find(
            ({ topicType, mode }) => topicType === type && mode === "command",
        );

        if (teleopGoalTopic && value.length > 0) {
            teleopGoalTopic.topicName = value;
        }

        if (teleopGoalTopic && value.length === 0) {
            rosStreams = rosStreams.filter(_ => _.topicType !== type);
        }

        if (!teleopGoalTopic && value.length > 0) {
            rosStreams.push({
                topicName: value,
                topicType: type,
                mode: "command",
            });
        }

        onChange({
            ...document,
            teleop: {
                ...teleop,
                rosStreams,
            },
        });
    };
}

function deviceFileTypeToString(type: DeviceFileType): string {
    switch (type) {
        case "file":
            return "File";
        case "image":
            return "Image";
        case "video":
            return "Video";
        case "point-cloud":
            return "Point cloud";
    }
}

function getRosVersionLabel(version: string): string {
    switch (version) {
        case "ROS-1":
            return "ROS 1";
        case "ROS-2":
            return "ROS 2";
    }
    return version;
}
